import type { DestroyRef, WritableSignal } from '@angular/core';
import { afterNextRender, inject, Injectable, Injector, runInInjectionContext, signal, untracked } from '@angular/core';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { createColumnForm, type ColumnForm } from '@sites/entity-signal-form';

import type { NormalizedColumnEntity } from '@web/types';

import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { HistoryFacade } from '@web/store/history/history.facade';

type ColumnFormEntry = {
    form: ColumnForm;
    entitySignal: WritableSignal<NormalizedColumnEntity>;
    storeSub: Subscription;
    refCount: number;
};

/** The column fields a form edit can touch — the envelope stays the store's. */
const EDITABLE_KEYS = ['props'] as const satisfies readonly (keyof NormalizedColumnEntity)[];

const pickEditable = (column: NormalizedColumnEntity | undefined): Partial<NormalizedColumnEntity> => {
    if (!column) return {};

    const changes: Record<string, unknown> = {};
    for (const key of EDITABLE_KEYS) {
        if (key in column) {
            changes[key] = (column as Record<string, unknown>)[key];
        }
    }

    return changes as Partial<NormalizedColumnEntity>;
};

/**
 * Ref-counted registry of column signal forms, the sibling of
 * {@link WebSectionFormService}. The form drives the persisted column JSON
 * directly (v0 — see the `column` entity in `@sites/entity-core`); the store
 * keeps owning identity, the section link and the child list, so only `props` is
 * dispatched back.
 */
@Injectable({ providedIn: 'root' })
export class WebColumnFormService {
    private readonly injector = inject(Injector);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private readonly registry = new Map<string, ColumnFormEntry>();

    public getOrCreate(columnId: string): ColumnForm {
        const existing = this.registry.get(columnId);
        if (existing) {
            existing.refCount++;
            return existing.form;
        }

        const entitySignal = signal<NormalizedColumnEntity>(undefined as any);

        // NgRx store.select() emits synchronously, so entitySignal is populated immediately
        const storeSub = this.columnsFacade
            .columnById$(columnId)
            .pipe(filter(Boolean))
            .subscribe((value) => entitySignal.set(value));

        const initialEntity = entitySignal();

        const form = runInInjectionContext(this.injector, () =>
            createColumnForm({
                entity: entitySignal,
                // The dispatcher picks the subtype factory off the entity signal,
                // which the store has already populated synchronously.
                onUpdate: (value: NormalizedColumnEntity) => {
                    const changes = pickEditable(value);

                    // A batch entry, not a change of its own: the v2 collector keys
                    // entries by entity, keeps the first `before` and the latest
                    // `after`, and flushes them together — so a slider drag is one
                    // undo step, and an edit that touches the row and its columns at
                    // once is undone as one thing.
                    this.historyFacade.addBatchUpdateToHistory(
                        'column',
                        { id: value.id, changes: pickEditable(untracked(entitySignal)) },
                        { id: value.id, changes }
                    );

                    this.columnsFacade.updateColumnProperties(value.id, changes);
                },
            })
        );

        // Sync immediately with the store value to avoid an empty model before the effect fires
        if (initialEntity) {
            form.syncFromEntity(structuredClone(initialEntity) as never);
        }

        this.registry.set(columnId, { form, entitySignal, storeSub, refCount: 1 });
        return form;
    }

    /**
     * Writes through the form for a caller that has no lifetime of its own — an
     * NgRx effect, say — and lets the reference go once the write has been
     * dispatched.
     *
     * The release cannot be inline: the form dispatches from an Angular `effect`,
     * so dropping the last reference on the next statement would destroy it before
     * the write ever left. `afterNextRender` runs after effects have flushed. When
     * a panel is already holding the form — the usual case, since the modal that
     * triggers this was opened from one — the ref-count never reaches zero anyway.
     */
    public writeThrough(columnId: string, apply: (form: ColumnForm) => void): void {
        const form = this.getOrCreate(columnId);

        apply(form);

        afterNextRender(() => this.release(columnId), { injector: this.injector });
    }

    public getOrCreateScoped(columnId: string, destroyRef: DestroyRef): ColumnForm {
        const form = this.getOrCreate(columnId);
        destroyRef.onDestroy(() => this.release(columnId));
        return form;
    }

    public getForm(columnId: string): ColumnForm | null {
        return this.registry.get(columnId)?.form ?? null;
    }

    public release(columnId: string): void {
        const entry = this.registry.get(columnId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(columnId);
        }
    }

    public deleteForm(columnId: string): void {
        const entry = this.registry.get(columnId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(columnId);
    }
}
